'use client';

import {
  Button,
  Icon,
  Slideout,
  SlideoutBody,
  SlideoutHeader,
  SlideoutHeaderActions,
  SlideoutHeaderBreadcrumb,
  SlideoutHeaderButtons,
  SlideoutHeaderClose,
  SlideoutHeaderHeading,
  SlideoutHeaderTitle,
  SlideoutSideNav,
  SlideoutSideNavItem,
} from '@lulalife/design-system';
import {
  Edit02,
  FileCheck02,
  Flash,
  Folder,
  Home03,
  InfoCircle,
  MessageTextSquare01,
  ReceiptCheck,
  Tool01,
  Truck01,
  XClose,
} from '@lulalife/design-system/icons/line';
import { type MouseEvent, type ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { DSSurface } from './DSSurface';

/**
 * The work order (and service request) slideout's tab rail and header on DS parts (D3b, decision
 * D15-30): Figma "Foresight Coordinator Baseline", `Slideout / Side Nav` and `Slideout / Header`
 * (section 7686:9093). Today's tabs, order, test ids and click behaviour stay (D15-32); only the
 * parts drawing them are the DS's. Still inside today's two-column Offcanvas; the 720 px shell is
 * a later package.
 */

/**
 * Each tab's DS line icon, by today's section slug: the Work Order Side Nav (Figma 7460:2364). The
 * service request's rail shares the Activity and Chat icons.
 */
const RAIL_ICONS: Record<string, ReactNode> = {
  overview: <Home03 />,
  details: <InfoCircle />,
  activityLog: <Flash />,
  workOrders: <Truck01 />,
  trips: <Truck01 />,
  quote: <FileCheck02 />,
  invoices: <ReceiptCheck />,
  chat: <MessageTextSquare01 />,
  property: <Edit02 />,
  stagingTools: <Tool01 />,
};

export type SlideoutRailTab = {
  slug: string;
  title: string;
  testId: string;
  count?: number;
  hideCount?: boolean;
};

type SlideoutRailProps<Tab extends SlideoutRailTab> = {
  /** The legacy column class around the rail (`xl-data__panel`), which places it. */
  className?: string;
  tabs: ReadonlyArray<Tab>;
  isActive: (slug: string) => boolean;
  onSelect: (tab: Tab) => void;
};

type SlideoutRailItemProps = {
  tab: SlideoutRailTab;
  active: boolean;
  onSelect: () => void;
};

/**
 * One tab. The DS draws the count badge and takes no props for it, so the badge gets today's
 * `{testId}-count` test id here (the old rail's `counter-icon` carried it; Playwright reads it).
 */
const SlideoutRailItem = ({ tab, active, onSelect }: SlideoutRailItemProps) => {
  // On the icon's wrapper: the DS item takes no ref, so the badge is found from inside its button.
  const iconRef = useRef<HTMLSpanElement>(null);
  const count = !tab.hideCount && tab.count !== undefined ? tab.count : undefined;
  useLayoutEffect(() => {
    if (count === undefined) return;
    iconRef.current
      ?.closest('button')
      ?.querySelector(':scope > [role="img"]')
      ?.setAttribute('data-testid', `${tab.testId}-count`);
  }, [count, tab.testId]);

  return (
    <SlideoutSideNavItem
      icon={
        <span ref={iconRef} className="contents" data-testid={tab.testId}>
          {RAIL_ICONS[tab.slug] ?? <Folder />}
        </span>
      }
      label={tab.title}
      active={active}
      count={count}
      onClick={onSelect}
    />
  );
};

/**
 * The rail: DS `SlideoutSideNav` with a `SlideoutSideNavItem` per tab, labels shown at every
 * width as today. Each tab's old test id sits on its icon, so `button:has([data-testid=…])`
 * still finds the tab; the active tab carries `aria-current="page"`.
 */
export const SlideoutRail = <Tab extends SlideoutRailTab>({
  className,
  tabs,
  isActive,
  onSelect,
}: SlideoutRailProps<Tab>) =>
  tabs.length ? (
    <DSSurface className={className}>
      <SlideoutSideNav showLabels className="h-full" data-testid="nav-options">
        {tabs.map((tab) => (
          <SlideoutRailItem
            key={tab.slug}
            tab={tab}
            active={isActive(tab.slug)}
            onSelect={() => onSelect(tab)}
          />
        ))}
      </SlideoutSideNav>
    </DSSurface>
  ) : null;

type SlideoutPanelTitleProps = {
  title: string;
  titleTestId?: string;
  /** The heading's id, for the dialog's `aria-labelledby`. */
  id?: string;
  /** Today's "Service Request /" link back to the parent record; no `onClick`, no link. */
  parent?: { label: string; onClick?: (event: MouseEvent<HTMLElement>) => void; testId: string };
};

/** The title: DS `SlideoutHeaderTitle`, after today's link to the parent record when there is one. */
export const SlideoutPanelTitle = ({ title, titleTestId, id, parent }: SlideoutPanelTitleProps) => (
  <SlideoutHeaderTitle id={id}>
    {parent && (
      <>
        <span
          role="presentation"
          data-testid={parent.testId}
          className={parent.onClick ? 'cursor-pointer hover:text-text-link' : 'cursor-default'}
          onClick={parent.onClick}
        >
          {parent.label}
        </span>
        <span className="px-space-8 text-text-tertiary">/</span>
      </>
    )}
    <span data-testid={titleTestId}>{title}</span>
  </SlideoutHeaderTitle>
);

type SlideoutPanelHeaderProps = {
  /**
   * The legacy class the Offcanvas and the Playwright fixtures find the header by
   * (`offcanvas-header`); its legacy box styles are reset here.
   */
  className?: string;
  /** The header's own test id (today's `work-order-details.header-{id}`). */
  testId?: string;
  /** The title cluster: a `SlideoutPanelTitle`, then the record's IDs and tags. */
  heading: ReactNode;
  headingTestId?: string;
  /** Today's action buttons and menu, as they are. */
  actions?: ReactNode;
  /** The legacy class today's buttons are styled under (`xl-header-right`). */
  actionsClassName?: string;
  onClose: (event: MouseEvent<HTMLButtonElement>) => void;
  closeTestId: string;
};

/**
 * The header: DS `SlideoutHeader` with the folder icon and the title cluster in
 * `SlideoutHeaderHeading`, today's buttons in `SlideoutHeaderButtons`, and Close. Close is the
 * DS Button `SlideoutHeaderClose` draws (tertiary, icon only, "Close"), written out because that
 * part takes no test id.
 */
export const SlideoutPanelHeader = ({
  className,
  testId,
  heading,
  headingTestId,
  actions,
  actionsClassName,
  onClose,
  closeTestId,
}: SlideoutPanelHeaderProps) => (
  <DSSurface
    className={[className, 'block! min-h-0! shrink-0! border-b-0! p-0!'].filter(Boolean).join(' ')}
    data-testid={testId}
  >
    <SlideoutHeader>
      <SlideoutHeaderHeading
        leadingIcon={
          <Icon size="md" className="text-icon-secondary">
            <Folder />
          </Icon>
        }
        data-testid={headingTestId}
      >
        {heading}
      </SlideoutHeaderHeading>
      {actions && (
        <SlideoutHeaderButtons className={actionsClassName}>{actions}</SlideoutHeaderButtons>
      )}
      <SlideoutHeaderActions>
        <Button
          type="button"
          variant="tertiary"
          iconOnly
          aria-label="Close"
          data-testid={closeTestId}
          onClick={onClose}
        >
          <XClose strokeWidth={1.5} />
        </Button>
      </SlideoutHeaderActions>
    </SlideoutHeader>
  </DSSurface>
);

type SlideoutParentLinkProps = {
  label: string;
  /** No `onClick`, no link: the label shows as plain text. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  testId: string;
};

/**
 * The parent record's row above the title (Figma's "Service Request" over "Work Order", 7460:2364:
 * a 28 px tall Breadcrumb holding one 14/20 `breadcrumb/text` item); a link back when it has
 * somewhere to go. DS `Breadcrumb` draws a lone item as the current page (tertiary, not clickable),
 * so this is a button with the breadcrumb's tokens. Its type is important: Bootstrap's unlayered
 * reboot (`button { font: inherit }`) otherwise holds a plain button at the page's 16/26.
 */
export const SlideoutParentLink = ({ label, onClick, testId }: SlideoutParentLinkProps) => (
  <button
    type="button"
    data-testid={testId}
    disabled={!onClick}
    onClick={onClick}
    className="inline-flex min-h-7 items-center bg-transparent p-0 text-left text-breadcrumb-text desktop-body-md-base! enabled:cursor-pointer enabled:hover:text-breadcrumb-text-hover disabled:cursor-default"
  >
    {label}
  </button>
);

type SlideoutShellHeaderProps = {
  /** The header's own test id (today's `work-order-details.header-{id}`). */
  testId?: string;
  /** The row above the title: a `SlideoutParentLink`, for a record that has a parent. */
  breadcrumb?: ReactNode;
  /** The title cluster: a `SlideoutPanelTitle`, then the record's chip and tags. */
  heading: ReactNode;
  headingTestId?: string;
  /** The More menu, its toggle drawn by DS `SlideoutHeaderMore`. */
  more?: ReactNode;
  onClose: () => void;
  closeTestId: string;
};

/**
 * The header of the 720 px DS `Slideout` (B17, Figma 7684:27944): an optional parent row, folder icon, title
 * cluster, More and `SlideoutHeaderClose`, with no button row (Schedule Trip, Ready to Schedule
 * and Start Quality Check stay in the More menu). Close takes no test id, so today's id sits on a
 * wrapper around it.
 */
export const SlideoutShellHeader = ({
  testId,
  breadcrumb,
  heading,
  headingTestId,
  more,
  onClose,
  closeTestId,
}: SlideoutShellHeaderProps) => (
  <DSSurface className="w-full shrink-0" data-testid={testId}>
    <SlideoutHeader>
      {breadcrumb && <SlideoutHeaderBreadcrumb>{breadcrumb}</SlideoutHeaderBreadcrumb>}
      <SlideoutHeaderHeading
        leadingIcon={
          <Icon size="md" className="text-icon-secondary">
            <Folder />
          </Icon>
        }
        data-testid={headingTestId}
      >
        {heading}
      </SlideoutHeaderHeading>
      <SlideoutHeaderActions>
        {more}
        <span className="inline-flex" data-testid={closeTestId}>
          <SlideoutHeaderClose onClick={onClose} />
        </span>
      </SlideoutHeaderActions>
    </SlideoutHeader>
  </DSSurface>
);

type SlideoutShellProps = {
  /** A `SlideoutShellHeader`. */
  header: ReactNode;
  /** A notice for the whole panel (live updates paused): under the header, above every tab. */
  banner?: ReactNode;
  /** Legacy classes the body's own markup is styled under (`offcanvas-body`). */
  bodyClassName?: string;
  bodyTestId?: string;
  'data-testid'?: string;
  children: ReactNode;
};

/**
 * The 720 px DS `Slideout` (B17): header, then `SlideoutBody` around the work order's own
 * legacy body and the rail. It is deliberately NOT DS territory (no `data-lula-ds`), because that
 * legacy body sits inside and must keep its app styles; so the shell's guarded utilities (width,
 * border, shadow) are restated important to hold the DS values. Kept in this folder so Tailwind
 * scans and emits those classes. One scroll, with the title bar and rail always in view: the shell
 * is at least 800 tall (Figma's minimum, which the package doesn't ship), grows with its content up
 * to the window, and fits the window when that is shorter than 800. Whatever doesn't fit scrolls in
 * its own column (`.ls-offcanvas-ds-slideout` in `_offcanvas.scss`).
 */
export const SlideoutShell = ({
  header,
  banner,
  bodyClassName,
  bodyTestId,
  'data-testid': testId,
  children,
}: SlideoutShellProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Focus moves into the panel when it opens and goes back to what opened it when it closes. The
  // host Offcanvas leaves focus on the trigger behind the overlay. The panel itself takes focus
  // (tabIndex -1), not a button, so no control shows a ring or tooltip on open.
  useEffect(() => {
    const trigger = document.activeElement;
    // The host Offcanvas is hidden while it slides in, and a hidden element refuses focus, so try
    // each frame until the panel holds it (a second or so at most).
    let frame = 0;
    let tries = 0;
    const focusPanel = () => {
      const root = rootRef.current;
      root?.focus({ preventScroll: true });
      tries += 1;
      if (root && !root.contains(document.activeElement) && tries < 60) {
        frame = requestAnimationFrame(focusPanel);
      }
    };
    focusPanel();
    return () => {
      cancelAnimationFrame(frame);
      if (
        trigger instanceof HTMLElement &&
        trigger.isConnected &&
        !trigger.closest('[data-slideout-shell]')
      ) {
        trigger.focus({ preventScroll: true });
      }
    };
  }, []);

  return (
    <Slideout
      // `ref` reaches the div through the DS Slideout's rest props (React 19); its prop type omits it.
      {...({ ref: rootRef } as object)}
      tabIndex={-1}
      data-slideout-shell=""
      className="w-full! md:w-(--slideout-desktop-width)! h-auto! min-h-[min(800px,100dvh)]! max-h-dvh! border-0! border-l! border-border-subtle! shadow-lg! outline-none"
      data-testid={testId}
    >
      {header}
      {banner}
      <SlideoutBody
        className={[bodyClassName, 'w-full!'].filter(Boolean).join(' ')}
        data-testid={bodyTestId}
      >
        {children}
      </SlideoutBody>
    </Slideout>
  );
};
